import { useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert base64url -> Uint8Array for VAPID key
function urlBase64ToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Registers the service worker and (if VAPID configured server-side + user grants
 * permission) creates a PushSubscription and posts it to /api/push/subscribe.
 * Falls back to in-tab Notification API on browsers/setups without push.
 */
export function usePushNotifications() {
  const { isAuthenticated } = useAuth();

  const enable = useCallback(async () => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false;
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      // Ask permission (idempotent)
      if (typeof Notification === 'undefined') return false;
      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();
      if (perm !== 'granted') return false;

      // Try to get a VAPID key from backend; if empty, skip push-subscribe (in-tab only)
      const keyRes = await axios.get(`${API}/push/vapid_public_key`).catch(() => ({ data: {} }));
      const vapid = keyRes.data?.vapid_public_key;
      if (!vapid) return true; // permission granted; page-open Notification API still works

      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid),
      });
      await axios.post(`${API}/push/subscribe`, {
        subscription: sub.toJSON ? sub.toJSON() : sub,
        ua: navigator.userAgent,
      }, { withCredentials: true });
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Auto-attempt on login — permission prompt only appears on user gesture,
    // so this call is idempotent and silent until the user grants it elsewhere.
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
    }
  }, [isAuthenticated]);

  return { enable };
}

/**
 * Fire an in-tab notification when a DM arrives while the app is open.
 * Called by Dashboard whenever unread count increases.
 */
export function notifyInTab(title, body) {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, { body, icon: '/logo192.png', tag: 'playmold-dm' });
    n.onclick = () => { window.focus(); window.location.href = '/messages'; };
  } catch { /* ignore */ }
}
